import { DocType } from '__core/shared/model/global/Doc.enum';
import './.react';
import actionService from './action.service';
import dateTimeService from './dateTime.service';
import './dialog/.module';
import './event/.module';
import './googlemaps/.module';
import './text/.module';
import './ui/.module';
import validationService from './validation.service';

export default angular
  .module('st.Core', [
    'st.Core.Dialog',
    'st.Core.Event',
    'st.Core.Text',
    'st.Core.UI',
    'st.Core.React',
    'angular-clipboard',
    'angular-jwt',
    'ngFileUpload',
    'gridster',
    'ngStorage',
    'ngResource',
    'ngSanitize',
    'ngIdle',
    'ui.router'
  ])
  .factory('DocType', () => DocType)
  .factory('cookies', () => Cookies.noConflict())
  .factory('fileFactory', ($q, $http, $window, logger, uiService) => {
    /**
     * Download files in bulk.
     *
     * @param {Object} params
     * @param {String} params.url - API URL.
     * @param {String} params.name - File Name.
     * @param {Object} params.attachments - Attachments to be downloaded.
     * @returns {Promise}
     */
    const bulkDownload = (params) => {
      const deferred = $q.defer();
      if (!params || !params.attachments || !params.attachments.length) {
        logger.error('fileFactory', 'bulkDownload', 'Attachments are required!');
        deferred.reject();
      } else {
        logger.log('fileFactory', 'bulkDownload', 'Resolving data from API');
        $http
          .post(
            params.url,
            { attachments: params.attachments },
            {
              responseType: 'arraybuffer',
              transformResponse(data) {
                let zip;
                if (data) {
                  zip = new Blob([data], { type: 'application/octet-stream' });
                }
                return {
                  response: zip
                };
              }
            }
          )
          .then((data) => {
            saveAs(data.data.response, `${params.name.replace(/[^a-zA-Z0-9. ]+/gi, '')}.zip`);
          });
      }
      return deferred.promise;
    };

    /**
     * Get file download URL.
     *
     * @param {Object} params
     * @param {String} params.url - API URL.
     * @param {String} params.file - Original file name.
     * @param {String} params.name - Name to assign to the downloaded file.
     * @returns {Promise}
     */
    const getDownloadUrl = (params) => {
      const deferred = $q.defer();
      if (!params || !params.url || !params.file || !params.name) {
        logger.error(
          'fileFactory',
          'getDownloadUrl',
          'API URL, File Type and Name are required!'
        );
        deferred.reject();
      } else {
        logger.log('fileFactory', 'getDownloadUrl', 'Resolving data from API');
        $http
          .get(params.url, { params: { file: params.file, originalName: params.name } })
          .then(
            (payload) => {
              logger.debug(
                `fileFactory | getDownloadUrl | Resolved data from API ${payload.data}`
              );
              deferred.resolve({ url: payload.data, name: params.name, ttl: 60 });
            },
            (err) => {
              logger.error(`fileFactory | getDownloadUrl | Error while resolving data ${err}`);
              deferred.resolve();
            }
          );
      }
      return deferred.promise;
    };

    /**
     * Download file from URL.
     *
     * @param {Object} params
     * @param {Object} params.url - File download URL.
     * @param {Object} params.name - File name.
     * @param {Object} isPdf - Is pdf file to download.
     */
    const download = (params, isPdf) => {
      const deferred = $q.defer();
      if (!params || !params.url || !params.name) {
        logger.error('fileFactory', 'download', 'Download URL and File Name are required!');
        deferred.reject();
      } else {
        uiService.toast({
          text: {
            p1: 'Please wait',
            p2: params.name,
            p3: isPdf ? 'is being converted and downloaded' : 'is being downloaded'
          },
          progress: true,
          warn: true,
          delay: isPdf ? 8000 : 2500
        });
        // eslint-disable-next-line no-param-reassign
        $window.location.href = params.url;
        deferred.resolve();
      }
      return deferred.promise;
    };

    /**
     * Get file download video URL.
     *
     * @param {Object} params
     * @param {String} params.url - API URL.
     * @param {String} params.idAttachment - Attachment ID.
     * @param {String} params.file - Original file name.
     * @param {String} params.name - Name to assign to the downloaded file.
     * @returns {Promise}
     */
    const getDownloadVideoUrl = (params) => {
      const deferred = $q.defer();
      if (!params || !params.url || !params.idAttachment || !params.file || !params.name) {
        logger.error(
          'fileFactory',
          'getDownloadVideoUrl',
          'API URL, Attachment ID, File Type and Name are required!'
        );
        deferred.reject();
      } else {
        logger.log('fileFactory', 'getDownloadVideoUrl', 'Resolving data from API');
        $http
          .get(params.url, {
            params: {
              idAttachment: params.idAttachment,
              file: params.file,
              originalName: params.name
            }
          })
          .then(
            (payload) => {
              logger.debug(
                `fileFactory | getDownloadVideoUrl | Resolved data from API ${payload.data}`
              );
              deferred.resolve({ url: payload.data, name: params.name, ttl: 60 });
            },
            (err) => {
              logger.error(
                `fileFactory | getDownloadVideoUrl | Error while resolving data ${err}`
              );
              deferred.resolve();
            }
          );
      }
      return deferred.promise;
    };

    /**
     * Download video file from URL.
     *
     * @param {Object} params
     * @param {Object} params.url - File download video URL.
     * @param {Object} params.name - File name.
     */
    const downloadVideo = (params) => {
      const deferred = $q.defer();
      if (!params || !params.url || !params.name) {
        logger.error(
          'fileFactory',
          'downloadVideo',
          'Download Video URL and File Name are required!'
        );
        deferred.reject();
      } else {
        uiService.toast({
          text: {
            p1: 'Please wait',
            p2: params.name,
            p3: 'is being downloaded'
          },
          warn: true
        });
        // eslint-disable-next-line no-param-reassign
        $window.location.href = params.url;
        deferred.resolve();
      }
      return deferred.promise;
    };

    return {
      bulkDownload,
      getDownloadUrl,
      download,
      getDownloadVideoUrl,
      downloadVideo
    };
  })
  .factory('jsonFactory', ($q, $http, logger, DATA) => {
    var service = {
      getCountries: getCountries,
      getCurrencies: getCurrencies,
      getOccupations: getOccupations,
      getIndustries: getIndustries,
      getZones: getZones
    };
    return service;

    function getCountries() {
      var deferred = $q.defer();
      logger.log('jsonFactory', 'getCountries', 'Resolving data');
      $http.get(DATA.JSON.COUNTRIES).then(
        function (payload) {
          logger.debug('jsonFactory', 'getCountries', 'Resolved data');
          deferred.resolve(payload.data);
        },
        function (err) {
          logger.error('jsonFactory', 'getCountries', 'Error while resolving data', err);
          deferred.resolve([]);
        }
      );
      return deferred.promise;
    }

    function getCurrencies() {
      var deferred = $q.defer();
      logger.log('jsonFactory', 'getCurrencies', 'Resolving data');
      $http.get(DATA.JSON.CURRENCIES).then(
        function (payload) {
          logger.debug('jsonFactory', 'getCurrencies', 'Resolved data');
          deferred.resolve(payload.data);
        },
        function (err) {
          logger.error('jsonFactory', 'getCurrencies', 'Error while resolving data', err);
          deferred.resolve([]);
        }
      );
      return deferred.promise;
    }

    function getOccupations() {
      var deferred = $q.defer();
      logger.log('jsonFactory', 'getOccupations', 'Resolving data');
      $http.get(DATA.JSON.OCCUPATIONS).then(
        function (payload) {
          logger.debug('jsonFactory', 'getOccupations', 'Resolved data');
          deferred.resolve(payload.data);
        },
        function (err) {
          logger.error('jsonFactory', 'getOccupations', 'Error while resolving data', err);
          deferred.resolve([]);
        }
      );
      return deferred.promise;
    }

    function getIndustries() {
      var deferred = $q.defer();
      logger.log('jsonFactory', 'getIndustries', 'Resolving data');
      $http.get(DATA.JSON.INDUSTRIES).then(
        function (payload) {
          logger.debug('jsonFactory', 'getIndustries', 'Resolved data');
          deferred.resolve(payload.data);
        },
        function (err) {
          logger.error('jsonFactory', 'getIndustries', 'Error while resolving data', err);
          deferred.resolve([]);
        }
      );
      return deferred.promise;
    }

    function getZones() {
      var deferred = $q.defer();
      logger.log('jsonFactory', 'getZones', 'Resolving data');
      $http.get(DATA.JSON.ZONES).then(
        function (payload) {
          logger.debug('jsonFactory', 'getZones', 'Resolved data');
          deferred.resolve(payload.data);
        },
        function (err) {
          logger.error('jsonFactory', 'getZones', 'Error while resolving data', err);
          deferred.resolve([]);
        }
      );
      return deferred.promise;
    }
  })
  .service('dateTimeService', dateTimeService)
  .service('validationService', validationService)
  .service('actionService', actionService)
  .service('changesService', ($q, $state, $timeout, $rootScope, $mdUtil, logger, uiService) => {
    var autoSave = null;
    var callbackChange = null;
    var callbackFinish = null;
    var callbackInvalid = null;
    var callbackValidate = null;
    var changesCollection = [];
    var finishInterval = 0;
    var isInitialized = false;
    var isPristine = true;
    var useFinishOnInterval = false;
    var useFinishOnChange = false;
    var useProgress = true;
    var watcher = null;

    var service = {
      change: $mdUtil.debounce(change, 150),
      finish: finish,
      init: init,
      hasChange: hasChange,
      onChange: onChange,
      onFinish: onFinish,
      onInvalid: onInvalid,
      onValidate: onValidate,
      reset: reset,
      resetChanges: resetChanges
    };
    return service;

    /**
     * Trigger Change event and onChange callback if configured.
     *
     * @param {Object} [_params] - Optional parameters that describe the source that triggered
     *   the change.
     * @param {String} [_params.group] - Field group name that triggered the change.
     * @param {String} [_params.field] - Field name that triggered the change.
     *
     * @returns {Object}
     */
    function change(_params) {
      if (isInitialized) {
        var group = (_params && _params.group) || undefined;
        var field = (_params && _params.field) || undefined;
        logger.log('changesService', 'change', 'Triggering change', group, field);
        logChange(_params);
        if (isPristine) {
          logger.log('changesService', 'change', 'Model is no longer pristine', group, field);
          isPristine = false;
        }
        if (useFinishOnChange && !isPristine) {
          logger.log('changesService', 'change', 'Calling onFinish callback', group, field);
          finish();
        }
        if (useFinishOnInterval) {
          if (autoSave !== null) {
            logger.log('changesService', 'change', 'Removing previous saveOnInterval');
            $timeout.cancel(autoSave);
          }
          logger.log('changesService', 'change', 'Configuring saveOnInterval', group, field);
          autoSave = $timeout(function () {
            if (!isPristine) {
              logger.log('changesService', 'change', 'Triggering saveOnInterval');
              $q.when(finish()).then(function () {
                autoSave = null;
              });
            }
          }, finishInterval);
        }
        if (callbackChange) {
          logger.log('changesService', 'change', 'Calling onChange callback', group, field);
          return callbackChange(_params);
        }
      }
      return null;
    }

    /**
     * Trigger Finish event and onFinish callback if configured.
     *
     * @returns {Promise}
     */
    function finish() {
      if (isInitialized && callbackFinish) {
        logger.log('changesService', 'finish', 'Calling onValidate callback');
        var isValid = callbackValidate && callbackInvalid ? callbackValidate() : true;
        if (!isValid) {
          logger.log('changesService', 'finish', 'Calling onInvalid callback');
          callbackInvalid();
        } else {
          logger.log('changesService', 'finish', 'Calling onFinish callback');
          if (useProgress) {
            uiService.toast({
              text: {
                p1: 'Saving',
                p2: 'your',
                p3: 'changes ...'
              },
              progress: true,
              delay: 2500
            });
          }
          return $q.when(callbackFinish()).then(function (result) {
            isPristine = true;
            return result;
          });
        }
      }
      return null;
    }

    /**
     * Toggle document(s) active state.
     *
     * @param {Object} _params
     * @param {Boolean} [_params.useProgress] - Toggle using toast with progressbar.
     * @param {Boolean} [_params.useFinishOnChange] - Automatically trigger onFinish every time
     *   changes occur.
     * @param {Boolean} [_params.useFinishOnInterval] - Automatically trigger onFinish every
     *   given interval.
     * @param {Number} [_params.finishInterval] -Trigger onFinish every given interval.
     */
    function init(_params) {
      logger.log('changesService', 'init', 'Initializing');
      reset();
      if (_params) {
        if (_params.useProgress === true || _params.useProgress === false) {
          useProgress = _params.useProgress;
        }
        if (_params.useFinishOnChange === true || _params.useFinishOnChange === false) {
          useFinishOnChange = _params.useFinishOnChange;
        }
        if (
          (_params.useFinishOnInterval === true || _params.useFinishOnInterval === false) &&
          !useFinishOnChange
        ) {
          useFinishOnInterval = _params.useFinishOnInterval;
          finishInterval = _params.finishInterval || 3500;
        }
      }
      watcher = $rootScope.$on('$stateChangeStart', function ($event, toState, toParams) {
        logger.log('changesService', 'stateChangeStart', 'Disabling auto save');
        autoSave = null;
        if (!isPristine) {
          logger.log(
            'changesService',
            'stateChangeStart',
            'Disabling state-change and checking model validity'
          );
          $event.preventDefault();
          var isValid = callbackValidate && callbackInvalid ? callbackValidate() : true;
          if (isValid) {
            logger.log(
              'changesService',
              'stateChangeStart',
              'Model is valid, calling onFinish'
            );
            $q.when(finish()).then(function (result) {
              if (result && result.status && result.status === false) {
                logger.log(
                  'changesService',
                  'stateChangeStart',
                  'Model is not saved, not transitioning to new state'
                );
              } else {
                logger.log(
                  'changesService',
                  'stateChangeStart',
                  'Model is saved, transitioning to new state'
                );
                $timeout(function () {
                  watcher();
                  if (result.toState) {
                    $state.transitionTo(result.toState, result.toParams);
                  } else {
                    $state.transitionTo(toState, toParams);
                  }
                }, 250);
              }
            });
          } else {
            if (callbackInvalid) {
              callbackInvalid();
            }
          }
        }
      });
    }

    /**
     * Check if Change event is logged.
     *
     * @param {Object} _params - Parameters that describe the source that triggered the change.
     * @param {String} _params.group - Field group name that triggered the change.
     * @param {String} [_params.field] - Field name that triggered the change.
     */
    function hasChange(_params) {
      var groupName = _params.group || 'Default';
      if (_params.field) {
        var fieldName = _params.field || 'Unknown';
        changesCollection[groupName] = changesCollection[groupName] || {};
        return changesCollection[groupName][fieldName] || 0;
      } else {
        return _.reduce(
          changesCollection[groupName],
          function (m, n) {
            return m + n;
          },
          0
        );
      }
    }

    /**
     * Log Change event.
     *
     * @param {Object} _params - Parameters that describe the source that triggered the change.
     * @param {String} _params.group - Field group name that triggered the change.
     * @param {String} _params.field - Field name that triggered the change.
     */
    function logChange(_params) {
      if (_params && (_params.group || _params.field)) {
        var groupName = _params.group || 'Default';
        var fieldName = _params.field || 'Unknown';
        logger.log('changesService', 'logChange', 'Logging changes', groupName, fieldName);
        changesCollection[groupName] = changesCollection[groupName] || {};
        changesCollection[groupName][fieldName] =
          (changesCollection[groupName][fieldName] || 0) + 1;
      } else {
        logger.log('changesService', 'logChange', 'No changes information to log');
      }
    }

    /**
     * Configure onChange callback function.
     *
     * @param {Function} _callback - Callback function.
     */
    function onChange(_callback) {
      if (_callback) {
        logger.log('changesService', 'onChange', 'Initializing callback');
        callbackChange = _callback;
      }
    }

    /**
     * Configure onFinish callback function.
     *
     * @param {Function} _callback - Callback function.
     */
    function onFinish(_callback) {
      if (_callback) {
        logger.log('changesService', 'onFinish', 'Initializing callback');
        callbackFinish = _callback;
      }
    }

    /**
     * Configure onInvalid callback function.
     *
     * @param {Function} _callback - Callback function.
     */
    function onInvalid(_callback) {
      if (_callback) {
        logger.log('changesService', 'onInvalid', 'Initializing callback');
        callbackInvalid = _callback;
      }
    }

    /**
     * Configure onValidate callback function.
     *
     * @param {Function} _callback - Callback function.
     */
    function onValidate(_callback) {
      if (_callback) {
        logger.log('changesService', 'onValidate', 'Initializing callback');
        callbackValidate = _callback;
      }
    }

    /**
     * Reset service state.
     */
    function reset() {
      autoSave = null;
      callbackChange = null;
      callbackFinish = null;
      callbackInvalid = null;
      callbackValidate = null;
      changesCollection = [];
      finishInterval = 0;
      isInitialized = true;
      isPristine = true;
      useFinishOnInterval = false;
      useFinishOnChange = false;
      useProgress = true;
      if (watcher !== null) {
        watcher();
      }
      watcher = null;
    }

    /**
     * Reset changes state.
     */
    function resetChanges() {
      changesCollection = [];
    }
  })
  .service('dataService', ($q, $http, logger, authenticationService, DocType) => {
    const requestQueue = {};
    const validate = (obj, doc, isDocTypeRequired, isIdRequired, skipModelInitialization) => {
      let model = null;
      if (skipModelInitialization) {
        model = doc;
        model.docType = obj?.blank().docType;
      } else {
        model = obj?.build(doc);
      }
      /* eslint-disable no-underscore-dangle */
      return {
        isValid:
          authenticationService.isAuthenticated() &&
          model &&
          (isDocTypeRequired === true
            ? model?.docType &&
              model?.docType === DocType.enumValueOf(model?.docType?.name) &&
              model?.docType !== DocType.__struct
            : true) &&
          (isIdRequired === true ? _.isUuid(doc?.id) : true),
        apiUrl: `/api/v1/${model?.docType.toString().split('_').pop()}/`,
        model
      };
      /* eslint-enable no-underscore-dangle */
    };

    return {
      apiRequest: (
        cache,
        cacheKey,
        cacheIgnore,
        modelBuild,
        apiMethod,
        apiUrl,
        params,
        required,
        doNotUseParams
      ) => {
        const queueKey = _.uuid();
        const deferred = $q.defer();

        const config = params?.__requestConfiguration || undefined;
        const payload = doNotUseParams ? params?.params : params;
        /* eslint-disable no-underscore-dangle */
        if (payload) {
          delete payload.__requestConfiguration;
        }
        /* eslint-enable no-underscore-dangle */

        if (!authenticationService.isAuthenticated()) {
          logger.error('dataService', 'apiRequest', 'Not authorized to execute the request');
          logger.debug(apiUrl, params);
          $q.when(() => {}, deferred.reject());
          return deferred.promise;
        }
        if (required) {
          const hasRequired = _.every([].concat(required), (p) =>
            _.has(doNotUseParams ? payload : payload?.params, p)
          );
          if (!hasRequired) {
            logger.error(
              'dataService',
              'apiRequest',
              'Params do not contain all required properties'
            );
            logger.debug(apiUrl, params, payload, required);
            $q.when(() => {}, deferred.reject());
            return deferred.promise;
          }
        }
        const cacheEnabled = !cacheIgnore && cache && cacheKey;
        if (cacheEnabled && !!cache.get(cacheKey)) {
          logger.log('dataService', 'apiRequest', 'Resolving data from cache');
          logger.debug(apiUrl, params);
          return $q.when(
            _.map([].concat(cache.get(cacheKey)), (model) =>
              modelBuild ? modelBuild(model) : model
            )
          );
        }
        if (!requestQueue[queueKey]) {
          requestQueue[queueKey] = [];
          logger.log('dataService', 'apiRequest', 'Resolving data from API');
          logger.debug(apiUrl, params, payload);
          $q.when(apiMethod(apiUrl, payload, config)).then(
            (response) => {
              logger.log('dataService', 'apiRequest', 'Resolved data from API');
              logger.debug(apiUrl, response.data);
              if (cacheEnabled) {
                try {
                  cache.put(cacheKey, response.data);
                } catch (ignore) {
                  cache.removeAll();
                }
              }
              _.forEach(requestQueue[queueKey], (promise) => {
                if (!modelBuild) {
                  promise.resolve(response.data);
                } else {
                  promise.resolve(
                    _.map([].concat(response.data), (model) =>
                      modelBuild ? modelBuild(model) : model
                    )
                  );
                }
              });
              delete requestQueue[queueKey];
            },
            (err) => {
              logger.error(
                'dataService',
                'apiRequest',
                'Error while resolving data from API',
                err
              );
              deferred.resolve([]);
            }
          );
        } else {
          logger.log('dataService', 'apiRequest', 'Waiting for previously issued promise');
        }
        requestQueue[queueKey].push(deferred);
        return deferred.promise;
      },
      createDocument: (obj, doc, params) => {
        const deferred = $q.defer();
        const { isValid, apiUrl, model } = validate(obj, doc, true, false);

        if (!isValid) {
          logger.error('dataService', 'readDocument', 'Erroneous Document');
          logger.debug(doc);
          $q.when(() => {}, deferred.reject());
          return deferred.promise;
        }
        logger.log('dataService', 'createDocument', 'Resolving data from API');
        logger.debug('dataService', 'createDocument', 'Resolving data from API', apiUrl);
        logger.debug(model, params);
        $http.post(apiUrl, { params: model, additionalParams: params }).then(
          (payload) => {
            logger.debug('dataService', 'createDocument', 'Resolved data from API');
            logger.debug(payload.data);
            deferred.resolve(payload.data);
          },
          (err) => {
            logger.error('dataService', 'createDocument', 'Error while resolving data', err);
            deferred.reject();
          }
        );
        return deferred.promise;
      },
      deleteDocument: (obj, doc) => {
        const deferred = $q.defer();
        const { isValid, apiUrl, model } = validate(obj, doc, true, true);

        if (!isValid) {
          logger.error('dataService', 'readDocument', 'Erroneous Document');
          logger.debug(doc);
          $q.when(() => {}, deferred.reject());
          return deferred.promise;
        }
        logger.log('dataService', 'deleteDocument', 'Resolving data from API');
        logger.debug('dataService', 'deleteDocument', 'Resolving data from API', apiUrl);
        logger.debug(model.id);
        $http.delete(apiUrl, { params: { id: model.id } }).then(
          (payload) => {
            logger.debug('dataService', 'deleteDocument', 'Resolved data from API');
            logger.debug(payload.data);
            deferred.resolve(payload.data);
          },
          (err) => {
            logger.error('dataService', 'deleteDocument', 'Error while resolving data', err);
            deferred.reject();
          }
        );
        return deferred.promise;
      },
      readDocument: (obj, doc) => {
        const deferred = $q.defer();
        const { isValid, apiUrl, model } = validate(
          obj,
          doc,
          true,
          doc.docType?.name !== '__definition_dashboard'
        );

        if (!isValid) {
          logger.error('dataService', 'readDocument', 'Erroneous Document');
          logger.debug(doc);
          $q.when(() => {}, deferred.reject());
          return deferred.promise;
        }
        logger.log('dataService', 'readDocument', 'Resolving data from API');
        logger.debug('dataService', 'readDocument', 'Resolving data from API', apiUrl);
        logger.debug(model.id);
        $http
          .get(apiUrl, {
            params: {
              id: model.id,
              idCurrentOrganization: model.idCurrentOrganization,
              uploadedFile: model.uploadedFile
            }
          })
          .then(
            (payload) => {
              logger.debug('dataService', 'readDocument', 'Resolved data from API');
              logger.debug(payload.data);
              deferred.resolve(obj.build(payload.data));
            },
            (err) => {
              logger.error('dataService', 'readDocument', 'Error while resolving data', err);
              deferred.reject();
            }
          );
        return deferred.promise;
      },
      updateDocument: (obj, doc, params) => {
        const deferred = $q.defer();
        const { isValid, apiUrl, model } = validate(obj, doc, true, true, true);

        if (!isValid) {
          logger.error('dataService', 'readDocument', 'Erroneous Document');
          logger.debug(doc);
          $q.when(() => {}, deferred.reject());
          return deferred.promise;
        }
        logger.log('dataService', 'updateDocument', 'Resolving data from API');
        logger.debug('dataService', 'readDocument', 'Resolving data from API', apiUrl);
        logger.debug(model, params);
        $http
          .patch(apiUrl, {
            params: !params ? model : { document: model, options: params }
          })
          .then(
            (payload) => {
              logger.debug('dataService', 'updateDocument', 'Resolved data from API');
              logger.debug(payload.data);
              deferred.resolve(payload.data);
            },
            (err) => {
              logger.error('dataService', 'updateDocument', 'Error while resolving data', err);
              deferred.reject();
            }
          );
        return deferred.promise;
      }
    };
  })
  .service('mergeService', ($rootScope, $http, CacheFactory, dataService, MERGE) => {
    let cache;
    if (!CacheFactory.get(MERGE.CACHE.NAME)) {
      cache = CacheFactory(MERGE.CACHE.NAME, MERGE.CACHE.STORE);
    }
    const cacheKey = (params, requestName) =>
      JSON.stringify({
        idAccount: $rootScope.CurrentAccount.id,
        idOrganization: $rootScope.CurrentOrganization.id,
        type: requestName || MERGE.CACHE.NAME,
        params
      });
    return {
      getAvailableKeys: (params) =>
        dataService.apiRequest(
          cache,
          cacheKey(params),
          params?.ignoreCache === true,
          null,
          $http.get,
          MERGE.API.AVAILABLE_KEYS,
          {
            params
          }
        )
    };
  })
  .service('privilegeService', ($rootScope) => ({
    confirmAutomatedNotifications: () =>
      $rootScope.CurrentAccount.userInterface.confirmAutomatedNotifications,
    isTicketEditAllowed: () =>
      $rootScope.CurrentAccount.isAdministrator ||
      $rootScope.CurrentOrganization.permissions.usersCanEditDeals,
    isTicketMoveAllowed: () =>
      $rootScope.CurrentAccount.isAdministrator ||
      $rootScope.CurrentOrganization.permissions.usersCanMoveDeals,
    showTileDealClient: () => $rootScope.CurrentAccount.userInterface.showTileDealClient,
    showTileDealPartner: () => $rootScope.CurrentAccount.userInterface.showTileDealPartner
  }))
  .service('fileService', ($http, dataService, FILE) => ({
    preview: (params) =>
      dataService.apiRequest(null, null, true, null, $http.post, FILE.API.PREVIEW, { params })
  }))
  .provider('webpage', (INSTANCE) => {
    let defaultPageTitle = '';
    let currentPageTitle = '';
    return {
      $get: ($rootScope, authenticationService) => {
        return {
          pageTitle: (_title) => {
            if (!!_title) {
              currentPageTitle = `${_title} ${
                authenticationService.isAuthenticated()
                  ? ` (${$rootScope.CurrentOrganization.getName()})`
                  : ''
              } on ${INSTANCE.name}`;
            }
            return currentPageTitle || defaultPageTitle || INSTANCE.name;
          }
        };
      },
      pageTitleDefault: (_title, _append) => {
        defaultPageTitle = _append || false ? `${INSTANCE.name} ${_title}` : _title;
      }
    };
  })
  .provider('logger', () => {
    let isEnabled = true;
    return {
      $get: ($log, formatterService) => {
        const log = (_logType, _args) => {
          if (isEnabled) {
            const timeStamp = formatterService.supplant('{0} |', [
              moment().format('HH:mm:ss:SS')
            ]);
            if (_args.length > 1) {
              $log[_logType].call(
                null,
                timeStamp,
                Array.prototype.slice.apply(_args).join(' | ')
              );
            } else {
              $log[_logType].call(null, timeStamp, _args[0]);
            }
          }
        };
        return {
          debug: (...args) => {
            log('debug', args);
          },
          error: (...args) => {
            log('error', args);
          },
          info: (...args) => {
            log('info', args);
          },
          log: (...args) => {
            log('log', args);
          },
          warning: (...args) => {
            log('warn', args);
          }
        };
      },
      setEnabled: (_enabled) => {
        isEnabled = !!_enabled;
      }
    };
  })
  .controller('webPageController', ($scope, webpage) => {
    $scope.webpageProvider = webpage;
  })
  .constant('INSTANCE', window.instanceConfiguration)
  .constant('MERGE', {
    API: {
      AVAILABLE_KEYS: '/api/v1/merge/available-keys/'
    },
    CACHE: {
      NAME: 'mergeCache',
      STORE: {
        TTL: 60 * 1000,
        DELETION: 'aggressive',
        RECYCLE: 30 * 1000,
        STORAGE: 'sessionStorage'
      }
    }
  })
  .constant('FILE', {
    API: {
      PREVIEW: '/api/v1/integrations/documents/preview/'
    },
    ACCEPTABLE_FILE_EXTENSIONS: [
      'tiff',
      'tif',
      'gif',
      'png',
      'pdf',
      'jpg',
      'jpeg',
      'jpe',
      'doc',
      'dot',
      'docx',
      'dotx',
      'docm',
      'dotm',
      'xls',
      'xlt',
      'xla',
      'xlsx',
      'xltx',
      'xlsm',
      'xltm',
      'xlam',
      'xlsb',
      'ppt',
      'pot',
      'pps',
      'ppa',
      'pptx',
      'potx',
      'ppsx',
      'ppam',
      'pptm',
      'potm',
      'ppsm'
    ]
  })
  .constant('DATETIME', {
    DATE_FORMAT: 'LL',
    TIME_FORMAT: 'LTS'
  })
  .constant('EVENT', {
    ACCOUNT_ADD: 'evAccountAdd',
    ACCOUNT_DELETE: 'evAccountDelete',
    ACCOUNT_AVATAR_UPLOAD: 'evAccountAvatarUpload',
    ACCOUNT_AVATAR_UPLOADED: 'evAccountAvatarUploaded',
    ACCOUNT_PASSWORD_CHANGE: 'evAccountPasswordChange',
    ACCOUNT_PASSWORD_CHANGED: 'evAccountPasswordChanged',
    ALERT_DIALOG: 'evAlertDialog',
    ASSET_FINANCE_PROPOSAL_AMORTIZATION_SCHEDULE_CHANGE:
      'evAssetFinanceProposalAmortizationScheduleChange',
    ASSET_FINANCE_QUICK_QUOTE_PROCESSING: 'evAssetFinanceQuickQuoteProcessing',
    ASSET_DETAILS_ADDITIONAL_INFO: 'evAssetDetailsAdditionalInfo',
    ATTACHMENT_EDIT: 'evAttachmentEdit',
    ATTACHMENT_RENAME: 'evAttachmentRename',
    CAMPAIGN_VIEW: 'evCampaignView',
    CHANGED_DATA: 'evChangedData',
    CHECKLIST_EDIT: 'evChecklistEdit',
    CREDENTIALS_CHANGED: 'evCredentialsChanged',
    CREDENTIALS_EXPIRING: 'evCredentialsExpiring',
    CONFIRM_DIALOG: 'evConfirmDialog',
    COMMISSION_EDIT: 'evCommissionEdit',
    CONNECT_TOGGLE: 'evConnectToggle',
    CONNECT_EVENTS: 'evConnectEvents',
    CONNECT_ONLINE: 'evConnectOnline',
    CONNECT_OFFLINE: 'evConnectOffline',
    CONNECT_ACCOUNT_ONLINE: 'evConnectAccountOnline',
    CONNECT_ACCOUNT_OFFLINE: 'evConnectAccountOffline',
    CONNECT_CALL: 'evConnectCall',
    CONNECT_CALL_SENT: 'evConnectCallSent',
    CONNECT_CALL_RECEIVED: 'evConnectCallReceived',
    CONNECT_SMS: 'evConnectSms',
    CONNECT_SMS_SENT: 'evConnectSmsSent',
    CONNECT_SMS_RECEIVED: 'evConnectSmsReceived',
    CONTACT_DELETE: 'evContactDelete',
    DIALOG_CLIENT_PROFILE_VERSION_EDIT: 'evDialogClientProfileVersionEdit',
    DIALOG_DIVERSIFICATION_REFERRAL_EDIT: 'evDialogDiversificationReferralEdit',
    DIALOG_DELETE_CONFIRMATION: 'evDialogDeleteConfirmation',
    DIALOG_E_SIGN: 'evDialogESign',
    DIALOG_LABEL_SELECT: 'evDialogLabelSelect',
    DIALOG_STATUS_SELECT: 'evDialogStatusSelect',
    DIALOG_OPEN_BANKING: 'evDialogOpenBanking',
    DIALOG_SELECT_OPTION: 'evDialogSelectOption',
    DIALOG_URL_EDIT: 'evDialogUrlEdit',
    DIALOG_TEXT_EDIT: 'evDialogTextEdit',
    DOCUMENT_ADD: 'evDocumentAdd',
    DOCUMENT_RENAME: 'evDocumentRename',
    DRIVE_OPEN: 'evDriveOpen',
    EMAIL_SEND: 'evEmailSend',
    FIELD_EDIT: 'evFieldEdit',
    FILE_PREVIEW: 'evFilePreview',
    FORM_WARNING: 'evFormWarning',
    GROUPS_AND_BRANCHES_ADD: 'evGroupsAndBranchesAdd',
    GROUPS_AND_BRANCHES_EDIT: 'evGroupsAndBranchesEdit',
    GROUPS_AND_BRANCHES_MOVE: 'evGroupsAndBranchesMove',
    INPUT_INSERT_VALUE: 'evInputInsertValue',
    INTEGRATION_ERRORS: 'evIntegrationErrors',
    JOB_EDIT: 'evJobEdit',
    LABEL_EDIT: 'evLabelEdit',
    LENDER_DOCUMENT_LIBRARY: 'evLenderDocumentLibrary',
    LODGEMENT_RESPONSE_ERROR: 'evLodgementResponseError',
    MARKETING_EDIT: 'evMarketingEdit',
    MENTOR_ASSIGN: 'evMentorAssign',
    MERGE_DOCUMENTS: 'evMergeDocuments',
    MESSAGE_EDIT: 'evMessageEdit',
    MESSAGE_EDIT_TRIGGER: 'evMessageEditTrigger',
    MY_CONNECT_EDIT: 'evMyConnectEdit',
    NOTE_EDIT: 'evNoteEdit',
    ORGANIZATION_ADD: 'evOrganizationAdd',
    ORGANIZATION_CHANGE: 'evOrganizationChange',
    ORGANIZATION_LOGO_UPLOAD: 'evOrganizationLogoUpload',
    ORGANIZATION_LOGO_UPLOADED: 'evOrganizationLogoUploaded',
    QUICK_TOOL_ASSET_FINANCE_QUICK_QUOTE: 'evQuickToolAssetFinanceQuickQuote',
    QUICK_TOOL_ASSET_FINANCE_QUOTING_CALCULATOR: 'evQuickToolAssetFinanceQuotingCalculator',
    QUICK_TOOL_HOME_LOAN_FUNDING_WORKSHEET: 'evQuickToolHomeLoanFundingWorksheet',
    QUICK_TOOL_HOME_LOAN_MAXIMUM_BORROWING: 'evQuickToolHomeLoanMaximumBorrowing',
    QUICK_TOOL_HOME_LOAN_COMPARE_PRODUCTS: 'evQuickToolHomeLoanCompareProducts',
    QUICK_TOOL_HOME_LOAN_REFINANCE_ANALYSIS: 'evQuickToolHomeLoanRefinanceAnalysis',
    PARTNER_ASSIGN: 'evPartnerAssign',
    PHONE_ASSIGN: 'evPhoneAssign',
    PHONE_RENT: 'evPhoneRent',
    PHONE_UNRENT: 'evPhoneUnrent',
    REPORTS_OPEN: 'evReportsOpen',
    REPORTS_CLOSE: 'evReportsClose',
    RP_DATA_PROPERTY_REFRESH_DATA: 'evRpDataPropertyRefreshData',
    SCRIPT_EDIT: 'evScriptEdit',
    TICKET_VIEW_CONTACT_ADD: 'evTicketViewContactAdd',
    TOGGLE_CONNECTION: 'evToggleConnection',
    VIDEO_CONFERENCE_INVITE: 'evVideoConferenceInvite',
    WORKFLOW_DELETE: 'evWorkflowDelete',
    WORKFLOW_TICKET_MOVE: 'evWorkflowTicketMove'
  })
  .constant('SOCKET', {
    CONNECT: 'connect',
    DISCONNECT: 'disconnect',
    ORGANIZATION_JOIN: 'organizationJoin',
    ORGANIZATION_JOINED: 'organizationJoined',
    ORGANIZATION_LEAVE: 'organizationLeave',
    ORGANIZATION_LEFT: 'organizationLeft',
    WORKFLOW_OBSERVE: 'workflowObserve',
    WORKFLOW_TICKET_MOVE: 'workflowTicketMove',
    WORKFLOW_DISREGARD: 'workflowDisregard'
  })
  .constant('LIST', {
    data: [],
    grouping: {
      startingLetters: [],
      startingLetter: 'ALL'
    },
    ordering: {
      property: 'getName()',
      isReversed: false
    },
    records: {
      shown: 25,
      limit: 25
    }
  })
  .constant('DATA', {
    JSON: {
      COUNTRIES: '/assets/json/countries.json',
      CURRENCIES: '/assets/json/currencies.json',
      OCCUPATIONS: '/assets/json/occupations.json',
      INDUSTRIES: '/assets/json/industries.json',
      ZONES: '/assets/json/zones.json'
    }
  });
